class ProductGallerySync {
  constructor() {
    this.mainCarousel = document.querySelector('.js-carousel-main');
    this.thumbCarousels = document.querySelectorAll('.js-thumb-carousel-nav');
    this.thumbWrappers = document.querySelectorAll('.js-thumb-wrapper');
    this.thumbnails = document.querySelectorAll('.js-thumb-item');
    this.mainSlides = document.querySelectorAll('.js-carousel-main .carousel-cell');
    this.currentIndex = 0;
    this.isScrolling = false;
    this.mobileBreakpoint = 979;

    if (this.mainCarousel && this.thumbnails.length > 0) {
      this.init();
      this.handleResponsiveLayout();
      window.addEventListener('resize', this.handleResponsiveLayout.bind(this));
    }
  }

  handleResponsiveLayout() {
    const isMobile = window.innerWidth <= this.mobileBreakpoint;
    this.thumbWrappers.forEach((wrapper) => {
      if (isMobile) {
        wrapper.classList.add('is-mobile');
        wrapper.classList.remove('is-desktop');
      } else {
        wrapper.classList.add('is-desktop');
        wrapper.classList.remove('is-mobile');
      }
    });
  }

  init() {
    this.mainSlides.forEach((slide, index) => {
      if (!slide.dataset.slideIndex) slide.dataset.slideIndex = index;
    });

    this.thumbnails.forEach((thumb) => {
      thumb.addEventListener('click', (e) => {
        e.preventDefault();
        const index = parseInt(thumb.dataset.slideIndex);
        if (!isNaN(index)) this.onThumbnailClick(index);
      });
      thumb.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          const index = parseInt(thumb.dataset.slideIndex);
          if (!isNaN(index)) this.onThumbnailClick(index);
        }
      });
    });

    this.mainCarousel.addEventListener('scroll', () => this.onMainCarouselScroll());
    this.setupIntersectionObserver();

    if (window.Flickity && this.mainCarousel.classList.contains('flickity-enabled')) {
      const flickity = Flickity.data(this.mainCarousel);
      if (flickity) {
        flickity.select(0, false, false);
        flickity.on('change', (index) => this.updateThumbnailActive(index));
      }
    }

    setTimeout(() => this.updateThumbnailActive(0), 300);
  }

  setupIntersectionObserver() {
    const options = { root: this.mainCarousel, threshold: 0.5, rootMargin: '0px' };
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const slideIndex = parseInt(entry.target.dataset.slideIndex);
          if (!isNaN(slideIndex) && slideIndex !== this.currentIndex) {
            this.currentIndex = slideIndex;
            this.updateThumbnailActive(slideIndex);
          }
        }
      });
    }, options);
    this.mainSlides.forEach((slide) => this.observer.observe(slide));
  }

  onThumbnailClick(index) {
    this.currentIndex = index;
    this.scrollToSlide(index);
    this.updateThumbnailActive(index);
  }

  scrollToSlide(index) {
    if (index < 0 || index >= this.mainSlides.length) return;
    const targetSlide = this.mainSlides[index];

    if (window.Flickity && this.mainCarousel.classList.contains('flickity-enabled')) {
      const flickity = Flickity.data(this.mainCarousel);
      if (flickity) {
        flickity.select(index, false, false);
        return;
      }
    }

    // Detect Firefox
    const isFirefox = navigator.userAgent.toLowerCase().indexOf('firefox') > -1;
    targetSlide.scrollIntoView({
      behavior: isFirefox ? 'auto' : 'smooth',
      block: 'nearest',
      inline: 'start',
    });
  }


  onMainCarouselScroll() {
    if (this.isScrolling) return;
    this.isScrolling = true;
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      const newIndex = this.getCurrentSlideIndex();
      if (newIndex !== this.currentIndex) {
        this.currentIndex = newIndex;
        this.updateThumbnailActive(newIndex);
      }
      this.isScrolling = false;
    }, 100);
  }

  getCurrentSlideIndex() {
    if (window.Flickity && this.mainCarousel.classList.contains('flickity-enabled')) {
      const flickity = Flickity.data(this.mainCarousel);
      if (flickity) return flickity.selectedIndex;
    }
    let closestIndex = 0;
    let maxVisibility = 0;
    this.mainSlides.forEach((slide, index) => {
      const rect = slide.getBoundingClientRect();
      const containerRect = this.mainCarousel.getBoundingClientRect();
      const visibleWidth = Math.min(rect.right, containerRect.right) - Math.max(rect.left, containerRect.left);
      const visibleHeight = Math.min(rect.bottom, containerRect.bottom) - Math.max(rect.top, containerRect.top);
      const visibility = visibleWidth > 0 && visibleHeight > 0 ? visibleWidth * visibleHeight : 0;
      if (visibility > maxVisibility) {
        maxVisibility = visibility;
        closestIndex = index;
      }
    });
    return closestIndex;
  }

  updateThumbnailActive(index) {
    this.thumbnails.forEach((thumb) => {
      thumb.classList.remove('is-nav-selected', 'active');
      thumb.setAttribute('aria-selected', 'false');
    });
    const matchingThumbnails = Array.from(this.thumbnails).filter(
      (thumb) => parseInt(thumb.dataset.slideIndex) === index
    );
    matchingThumbnails.forEach((thumbnail) => {
      thumbnail.classList.add('is-nav-selected', 'active');
      thumbnail.setAttribute('aria-selected', 'true');
    });
    this.scrollThumbnailsIntoView(index);
  }

  scrollThumbnailsIntoView(index) {
    const matchingThumbnails = Array.from(this.thumbnails).filter(
      (thumb) => parseInt(thumb.dataset.slideIndex) === index
    );
    matchingThumbnails.forEach((thumbnail) => {
      const thumbWrapper = thumbnail.closest('.js-thumb-wrapper');
      const thumbCarousel = thumbnail.closest('.js-thumb-carousel-nav');
      if (!thumbWrapper) return;
      if (window.Flickity && thumbCarousel && thumbCarousel.classList.contains('flickity-enabled')) {
        const flickity = Flickity.data(thumbCarousel);
        if (flickity) {
          flickity.select(index, true, false);
          return;
        }
      }
      thumbnail.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    });
  }

  getThumbnailPosition() {
    if (this.thumbWrappers.length === 0) return 'left';
    const visibleWrapper = Array.from(this.thumbWrappers).find((wrapper) => wrapper.offsetParent !== null);
    if (!visibleWrapper) return 'left';
    if (visibleWrapper.classList.contains('horizontal-carousel')) return 'horizontal';
    const position = visibleWrapper.dataset.thumbnailPosition;
    return position || 'left';
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new ProductGallerySync());
} else {
  new ProductGallerySync();
}

document.addEventListener('variant:changed', () => {
  setTimeout(() => new ProductGallerySync(), 100);
});

document.addEventListener('shopify:section:load', () => {
  setTimeout(() => new ProductGallerySync(), 100);
});
